import {useState} from 'react'
import {color, Page, Spinner, Icons, PoweredBy} from '@bakkt/components'
import {BottomBox, Main} from 'components/Layout'
import Button from 'components/Button'
import {CurrencyFlipper} from 'components/CurrencyFlipper'
import {FundingAccountRow, CryptoAccountRow} from 'components/fundingRows'
import {TransactType, TransactTypeEnum, useStore} from 'store'
import {formatCurrency} from 'utils/currencyFormatters'
import {ReceiptItem} from 'components/ReceiptItem'
import {Receipt} from 'components/Receipt'
import {apiCall, embeddedPartnerApi} from 'api'
import {showDialog, showWarning} from 'components/Dialog'
import {RoutesName} from 'constants/routes'
import {t} from 'i18next'
import {ApiAmountCurrencyEnum, ApiEmbeddedCryptoTransactionRequestTransactTypeEnum, FiatAmount} from '@bakkt/api'
import {useSelectedCryptoAccountInfo} from 'hooks/useSelectedCryptoAccountInfo'
import {useNavigate, useCryptoCurrencyPrices} from 'hooks'
import {showTransactionErrorDialog} from 'components/ErrorDialog'

type Props = {
	transactionType: TransactTypeEnum
}
export const CryptoTransactionConfirm = ({transactionType}: Props) => {
	const {cryptoTransactionConfirm, partner, selectedFundingAccount, setCryptoTransaction} = useStore()
	const {selectedCryptoCurrencyName, selectedCryptoCurrency} = useSelectedCryptoAccountInfo()
	const {cryptoCurrencyPrice} = useCryptoCurrencyPrices()
	const navigate = useNavigate()
	const [loading, setLoading] = useState(false)

	const doTransaction = async () => {
		setLoading(true)
		const {response, error} = await apiCall(() =>
			embeddedPartnerApi.transactCryptoCurrency({
				transactType: (cryptoTransactionConfirm?.transactType ||
					'BUY') as ApiEmbeddedCryptoTransactionRequestTransactTypeEnum,
				quantity: {
					amount:
						cryptoTransactionConfirm?.quantity?.amount &&
						Number(cryptoTransactionConfirm?.quantity?.amount?.toFixed(8)),
					currency: cryptoTransactionConfirm?.quantity?.currency as ApiAmountCurrencyEnum,
				},
				price: cryptoTransactionConfirm?.price as FiatAmount,
				externalAccountRef: selectedFundingAccount?.externalAccountRef || '',
			}),
		)

		setLoading(false)
		if (error) {
			if (error.code !== 'PARTY_SUSPENDED') {
				showTransactionErrorDialog(partner)
			}
		} else {
			setCryptoTransaction(response as any)
			navigate(
				`/${transactionType === TransactType.BUY ? RoutesName.crypto.buy : RoutesName.crypto.sell}/${
					RoutesName.crypto.success
				}`,
				{replace: true, ignore: true},
			)
		}
	}

	return (
		<Page
			withBackIcon
			titleHeader={
				transactionType === TransactType.SELL
					? `Sell ${selectedCryptoCurrencyName}`
					: `Buy ${selectedCryptoCurrencyName}`
			}
		>
			{loading ? (
				<div style={{marginTop: 'auto', marginLeft: 'auto', marginRight: 'auto'}}>
					<Spinner />
				</div>
			) : (
				<Main>
					<CurrencyFlipper
						disabledInput
						fiat={cryptoTransactionConfirm?.totalPrice?.amount || 0}
						crypto={cryptoTransactionConfirm?.quantity?.amount || 0}
					/>

					{transactionType === TransactType.BUY && (
						<Receipt>
							<FundingAccountRow
								style={{padding: '0.4rem 0 0.8rem 0'}}
								label={t('label.fundingSource')}
							/>
							<ReceiptItem
								name={`${selectedCryptoCurrency} ${t('crypto.confirmation.price')}`}
								value={formatCurrency(
									cryptoCurrencyPrice?.indicativePrice?.amount || 0,
									cryptoTransactionConfirm?.totalPrice?.currency,
								)}
							/>
							<ReceiptItem
								name={t('crypto.confirmation.purchasePrice')}
								value={formatCurrency(
									cryptoTransactionConfirm?.price?.amount || 0,
									cryptoTransactionConfirm?.price?.currency,
								)}
							/>
							<ReceiptItem
								name={t('crypto.confirmation.fee')}
								value={formatCurrency(
									cryptoTransactionConfirm?.transactionFee?.amount || 0,
									cryptoTransactionConfirm?.transactionFee?.currency,
								)}
								onInfoClicked={() =>
									showDialog({
										title: t('crypto.confirmation.fees'),
										message: t('crypto.confirmation.spreadInfo'),
										hideSave: true,
										hideCancel: true,
										skip: true,
										skipText: t('btnOkGotIt'),
										skipButtonStyle: {color: color.blue700},
										icon: Icons.DialogInfo,
									})
								}
							/>
							<ReceiptItem
								style={{borderBottom: 'none'}}
								name={t('crypto.confirmation.totalCost')}
								value={formatCurrency(
									cryptoTransactionConfirm?.totalPrice?.amount || 0,
									cryptoTransactionConfirm?.totalPrice?.currency,
								)}
							/>
						</Receipt>
					)}
					{transactionType === TransactType.SELL && (
						<Receipt>
							<CryptoAccountRow style={{padding: '0.4rem 0 0.8rem 0'}} />
							<ReceiptItem
								name={t('crypto.confirmation.cryptoToSell', {
									cryptoCurrencyName: selectedCryptoCurrencyName,
								})}
								value={formatCurrency(
									cryptoTransactionConfirm?.quantity?.amount || 0,
									cryptoTransactionConfirm?.quantity?.currency,
								)}
							/>
							<ReceiptItem
								name={`${selectedCryptoCurrency} ${t('crypto.confirmation.price')}`}
								value={formatCurrency(
									cryptoCurrencyPrice?.indicativePrice?.amount || 0,
									cryptoTransactionConfirm?.totalPrice?.currency,
								)}
							/>
							<ReceiptItem
								name={t('crypto.confirmation.sellPrice')}
								value={formatCurrency(
									cryptoTransactionConfirm?.price?.amount || 0,
									cryptoTransactionConfirm?.price?.currency,
								)}
							/>
							<ReceiptItem
								name={t('crypto.confirmation.fee')}
								value={formatCurrency(
									cryptoTransactionConfirm?.transactionFee?.amount || 0,
									cryptoTransactionConfirm?.transactionFee?.currency,
								)}
								onInfoClicked={() =>
									showDialog({
										title: t('crypto.confirmation.fees'),
										message: t('crypto.confirmation.spreadInfo'),
										hideSave: true,
										hideCancel: true,
										skip: true,
										skipText: t('btnOkGotIt'),
										skipButtonStyle: {color: color.blue700},
										icon: Icons.DialogInfo,
									})
								}
							/>
							<ReceiptItem
								style={{borderBottom: 'none'}}
								name={t('crypto.confirmation.totalProceeds')}
								value={formatCurrency(
									cryptoTransactionConfirm?.totalPrice?.amount || 0,
									cryptoTransactionConfirm?.totalPrice?.currency,
								)}
							/>
							<FundingAccountRow
								label={t('label.destination')}
								style={{
									borderBottom: 'none',
									borderTop: '5px solid',
									borderTopColor: color.grey100,
									padding: '0.4rem 0.4rem 0.8rem 0.8rem',
								}}
							/>
						</Receipt>
					)}
				</Main>
			)}
			<BottomBox>
				<Button
					id='crypto-transaction-confirm-button'
					onClick={doTransaction}
					loading={loading}
					disabled={loading || !selectedFundingAccount || !cryptoTransactionConfirm?.price}
				>
					{transactionType === TransactType.BUY
						? t('crypto.confirmation.buyNow')
						: t('crypto.confirmation.sellNow')}
				</Button>
				<PoweredBy text={t('poweredBy')} />
			</BottomBox>
		</Page>
	)
}
